/**
 * Challenge Hub — monthly challenges + badges.
 * Backend:
 *   GET  /api/challenges          -> active challenges + user progress
 *   POST /api/challenges/progress -> bump progress
 *   GET  /api/challenges/badges/mine
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, Target, Award, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function ChallengeCard({ ch, onBump }) {
  const pct = Math.min(100, Math.round((ch.user_progress / ch.action_target) * 100));
  const done = ch.user_completed;
  return (
    <Card className={`relative overflow-hidden ${done ? "bg-primary/5 border-primary/40" : ""}`} data-testid={`challenge-${ch.slug}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-secondary" />
              <span className="overline text-secondary">{ch.action_type.replace(/_/g, " ")}</span>
            </div>
            <div className="mt-1 font-display text-xl font-medium">{ch.name}</div>
            <p className="text-sm text-muted-foreground mt-1">{ch.description}</p>
          </div>
          {done && <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-mono text-muted-foreground">{ch.user_progress} / {ch.action_target}</span>
            <span className="font-semibold">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-xs"><Sparkles className="w-3 h-3 mr-1" /> +{ch.reward_gems} Gems</Badge>
          {ch.reward_badge && <Badge className="bg-secondary/20 text-secondary text-xs">🏅 {ch.reward_badge}</Badge>}
          <div className="ml-auto text-xs text-muted-foreground">
            Ends {new Date(ch.ends_at).toLocaleDateString("en-GB")}
          </div>
        </div>

        {!done && (
          <Button
            size="sm" variant="ghost"
            className="mt-3 text-xs"
            onClick={() => onBump(ch)}
            data-testid={`challenge-bump-${ch.slug}`}
          >
            Log progress (+1)
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        api.get("/challenges"),
        api.get("/challenges/badges/mine"),
      ]);
      setChallenges(c.data.challenges || []);
      setBadges(b.data.badges || []);
    } catch (e) {
      toast.error("Failed to load challenges");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const bump = async (ch) => {
    try {
      const r = await api.post("/challenges/progress", { challenge_id: ch.id, increment: 1 });
      if (r.data.completed) {
        toast.success(`Challenge complete! +${r.data.gems_awarded} Gems${r.data.badge ? ` + ${r.data.badge} badge` : ""}`);
      } else {
        toast.success(`Progress logged: ${r.data.progress}/${r.data.target}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log progress");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="challenges-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-secondary flex items-center gap-2"><Trophy className="w-3 h-3" /> Live challenges</div>
          <h1 className="text-3xl font-display font-light mt-1">Challenge Hub</h1>
          <p className="mt-2 text-muted-foreground">Complete challenges, earn Gems, unlock badges.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading challenges…</div>
      ) : challenges.length === 0 ? (
        <Card><CardContent className="text-center py-14 text-muted-foreground">
          No active challenges right now. Check back next month.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {challenges.map(ch => <ChallengeCard key={ch.id} ch={ch} onBump={bump} />)}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4" /> Your badges ({badges.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {badges.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No badges yet. Complete a challenge to earn your first badge.
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {badges.map(b => (
                <div key={b.badge_slug} className="border border-border rounded-xl px-4 py-3 bg-muted/40" data-testid={`badge-${b.badge_slug}`}>
                  <div className="text-2xl">🏅</div>
                  <div className="text-sm font-semibold mt-1">{b.badge_slug}</div>
                  <div className="text-[10px] text-muted-foreground uppercase mt-0.5">
                    {new Date(b.earned_at).toLocaleDateString("en-GB")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
